import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useToaster } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { IApiResponse, ISystemCodeDto } from '../../../utils/constants/interfaces';
import { ToastStyle } from '../../../utils/hooks/useToaster';

const defaultSystemCode: ISystemCodeDto = {
  id: 0,
  codeType: '',
  codeValue: '',
  description: '',
  info: '',
  active: true,
  sortOrder: 0,
  createdBy: '',
  dateCreatedUtc: new Date(0),
  lastModifiedBy: '',
  lastModifiedDateUtc: new Date(0)
};

const useSystemCodeEditor = (systemCodeId: string | null) => {
  const api = useApi();
  const navigate = useNavigate();
  const toaster = useToaster();

  const [systemCode, setSystemCode] = useState<ISystemCodeDto>(defaultSystemCode);
  const [originalSystemCode, setOriginalSystemCode] = useState<ISystemCodeDto>(defaultSystemCode); // New state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [loadingSystemCode, setLoadingSystemCode] = useState<boolean>(true);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState<boolean>(false);

  const loadSystemCode = useCallback(
    async (systemCodeId: string | null): Promise<void> => {
      if (!Helpers.isNullOrWhiteSpace(systemCodeId)) {
        if (systemCodeId === 'new') {
          setSystemCode(defaultSystemCode);
          setOriginalSystemCode(defaultSystemCode); // Set original for new codes
          setIsDirty(false);
        } else {
          setFieldErrors([]);
          setLoadingSystemCode(true);
          const endpoint = `/codes/${systemCodeId}`;
          const apiResponse = await api.get<ISystemCodeDto>(endpoint);
          if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
            setSystemCode(apiResponse.response);
            setOriginalSystemCode(apiResponse.response); // Set original for existing codes
          } else {
            await navigate('/codes?error=loading+code');
          }
        }
        setLoadingSystemCode(false);
      }
    },
    [api, navigate]
  );

  const updateValue = (key: string, value: string | number | boolean): void => {
    if (isSaving || !systemCode) return;
    setIsDirty(true);

    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    const updatedSystemCode = { ...systemCode, [key]: value };
    setSystemCode(updatedSystemCode);
  };

  const validate = (): boolean => {
    const errors: string[] = [];
    if (Helpers.isNullOrWhiteSpace(systemCode.codeType)) {
      errors.push('overview:codeType');
    }
    if (Helpers.isNullOrWhiteSpace(systemCode.codeValue)) {
      errors.push('overview:codeValue');
    }
    setFieldErrors(errors);
    return errors.length === 0;
  };

  const performSave = async (): Promise<IApiResponse<ISystemCodeDto> | null> => {
    const isValid = validate();
    if (!isValid) {
      toaster.addToast('Unable to save — required fields are missing.', ToastStyle.ERROR, 5000);
      Helpers.scrollToClassName('is-invalid', -20);
      return null;
    }
    setIsSaving(true);
    let apiResponse: IApiResponse<ISystemCodeDto>;
    try {
      const isNew = systemCode.id === 0;
      if (isNew) {
        const endpoint = `/codes`;
        apiResponse = await api.post<ISystemCodeDto>(endpoint, JSON.stringify(systemCode));
      } else {
        const endpoint = `/codes/${systemCode.id}`;
        apiResponse = await api.put<ISystemCodeDto>(endpoint, JSON.stringify(systemCode));
      }
      return apiResponse;
    } finally {
      setIsSaving(false);
    }
  };

  const saveChanges = async (): Promise<void> => {
    const apiResponse = await performSave();
    if (apiResponse === null) {
      return; // Validation failed
    }
    if (apiResponse.status?.statusCode === 200 && apiResponse.response) {
      setIsDirty(false);
      toaster.addToast('Code saved successfully!', ToastStyle.SUCCESS, 2000);

      // Handle both create and update response formats
      let savedCodeId: number | undefined;
      if ('systemCodeId' in apiResponse.response) {
        savedCodeId = Number(apiResponse.response.systemCodeId);
      } else if ('id' in apiResponse.response) {
        savedCodeId = Number(apiResponse.response.id);
      }

      if (savedCodeId && savedCodeId > 0) {
        await navigate(`/codes/${savedCodeId}`);
      } else {
        toaster.addToast('Error: Invalid code ID returned from server', ToastStyle.ERROR, 8000);
      }
    } else {
      toaster.addToast('Error saving code', ToastStyle.ERROR, 8000);
    }
  };

  const saveAndBack = async (): Promise<void> => {
    const apiResponse = await performSave();
    if (apiResponse === null) {
      return; // Validation failed
    }
    if (apiResponse.status?.statusCode === 200 && apiResponse.response) {
      toaster.addToast('Code saved successfully!', ToastStyle.SUCCESS, 2000);
      await navigate('/codes');
    } else {
      toaster.addToast('Error saving code', ToastStyle.ERROR, 8000);
    }
  };

  const saveAndNew = async (): Promise<void> => {
    const apiResponse = await performSave();
    if (apiResponse === null) {
      return; // Validation failed
    }
    if (apiResponse.status?.statusCode === 200 && apiResponse.response) {
      toaster.addToast('Code saved successfully!', ToastStyle.SUCCESS, 2000);
      // Always reset the form to a blank state after saving
      setSystemCode(defaultSystemCode);
      setOriginalSystemCode(defaultSystemCode);
      setIsDirty(false);
      setFieldErrors([]);
      // Navigate to /codes/new only if editing an existing system code
      if (systemCodeId !== 'new') {
        await navigate('/codes/new');
      }
    } else {
      toaster.addToast('Error saving code', ToastStyle.ERROR, 8000);
    }
  };

  const cancelChanges = useCallback(() => {
    setSystemCode(originalSystemCode);
    setIsDirty(false);
    setFieldErrors([]);
  }, [originalSystemCode]);

  const back = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
      return;
    }
    setSystemCode(defaultSystemCode);
    void navigate('/codes');
  };

  const closeUnsavedChangesDialog = () => {
    setShowUnsavedChangesDialog(false);
  };

  const discardUnsavedChanges = () => {
    setShowUnsavedChangesDialog(false);
    setIsDirty(false);
    setSystemCode(defaultSystemCode);
    void loadSystemCode(systemCodeId);
  };

  useEffect(() => {
    void loadSystemCode(systemCodeId);
  }, [loadSystemCode, systemCodeId]);

  return {
    systemCode,
    isSaving,
    fieldErrors,
    loadingSystemCode,
    showUnsavedChangesDialog,
    back,
    updateValue,
    saveChanges,
    saveAndBack,
    saveAndNew,
    discardUnsavedChanges,
    closeUnsavedChangesDialog,
    cancelChanges,
    ToastContainer: toaster.ToastContainer
  };
};

export default useSystemCodeEditor;
